import { ProFormDatePicker } from '@ant-design/pro-components'
import { t } from '@lingui/core/macro'
import { Grid } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import type React from 'react'
import { EditableLabel } from '../components/EditableLabel'
import { EditableShell } from '../components/EditableShell'
import { useFieldContext } from '../createAppFormHook'
import { useEditableFieldMode } from '../hooks/useEditableFieldMode'
import { useMergedFieldProps } from '../utils/mergeFieldProps'

export type DatePickerFieldProps = Omit<React.ComponentProps<typeof ProFormDatePicker>, 'className'> & {
  className?: string
}

export function DatePickerField(props: DatePickerFieldProps) {
  const fieldCtx = useFieldContext<string | undefined>()
  const { formItemProps, fieldProps, proFieldProps, className, ...rest } = props
  const { xxl } = Grid.useBreakpoint()

  const editable = useEditableFieldMode<Dayjs | undefined>()

  const merged = useMergedFieldProps({
    fieldCtx,
    editable,
    props: props as never,
    fieldDefaults: {
      fieldProps: {
        onChange: (value: Dayjs | null) => {
          fieldCtx.handleChange(value ? value.format('YYYY-MM-DD') : undefined)
        },
        // The datepicker does not like the onBlur. Picking a date will not call onChange then
        onBlur: undefined,
        // disables typing dates on mobile screenings (approx. < 1440px) to stop the keyboard from popping up
        inputReadOnly: !xxl,
        format: [t`DD.MM.YYYY`],
      },
    },
  })

  const value = fieldCtx.state.value ? dayjs(fieldCtx.state.value, 'YYYY-MM-DD') : null

  return (
    <EditableShell
      mode={editable.mode}
      enterEdit={editable.enterEdit}
      exitRead={editable.exitRead}
      className={className}
    >
      <ProFormDatePicker
        {...rest}
        picker="date"
        label={
          rest.label && (
            <EditableLabel
              mode={editable.mode}
              enterEdit={editable.enterEdit}
              exitRead={editable.exitRead}
              onUndo={editable.undoAndExit}
              canUndo={editable.canUndo}
            >
              {rest.label}
            </EditableLabel>
          )
        }
        proFieldProps={merged.proFieldProps}
        formItemProps={merged.formItemProps}
        fieldProps={{
          ...merged.fieldProps,
          value,
        }}
      />
    </EditableShell>
  )
}
